import { useCallback, useEffect, useRef, useState } from "react";

interface UseMultipleScreensDetectionOptions {
  pollIntervalMs?: number;
  enabled?: boolean;
  autoStart?: boolean;
}

/**
 * Best-effort detection for multiple connected screens via the Screen Details API.
 * Returns a warning flag that can be used to block flows when more than one screen is present.
 */
export const useMultipleScreensDetection = ({
  pollIntervalMs = 5000,
  enabled = true,
  autoStart = true,
}: UseMultipleScreensDetectionOptions = {}) => {
  const [hasMultipleScreens, setHasMultipleScreens] = useState(false);
  const [isScreenDetailsSupported, setIsScreenDetailsSupported] = useState(false);
  const cleanupRef = useRef<(() => void) | null>(null);

  const stopDetection = useCallback(() => {
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
  }, []);

  const startDetection = useCallback(async () => {
    stopDetection();

    if (!enabled) {
      setHasMultipleScreens(false);
      setIsScreenDetailsSupported(false);
      return;
    }

    if (typeof window === "undefined") {
      return;
    }

    if (!("getScreenDetails" in window)) {
      setIsScreenDetailsSupported(false);
      setHasMultipleScreens(false);
      return;
    }

    setIsScreenDetailsSupported(true);

    try {
      const screenDetails = await (window as any).getScreenDetails();
      const updateScreenCount = () => {
        const screensLength = screenDetails?.screens?.length ?? 0;
        setHasMultipleScreens(screensLength > 1);
      };

      updateScreenCount();

      const handleScreensChange = () => {
        updateScreenCount();
      };

      screenDetails?.addEventListener?.("screenschange", handleScreensChange);
      screenDetails?.addEventListener?.("currentscreenchange", handleScreensChange);

      const intervalId = setInterval(updateScreenCount, pollIntervalMs);

      cleanupRef.current = () => {
        clearInterval(intervalId);
        screenDetails?.removeEventListener?.("screenschange", handleScreensChange);
        screenDetails?.removeEventListener?.("currentscreenchange", handleScreensChange);
      };
    } catch (error) {
      setHasMultipleScreens(false);
      cleanupRef.current = null;
    }
  }, [enabled, pollIntervalMs, stopDetection]);

  useEffect(() => {
    if (!autoStart) {
      return undefined;
    }

    startDetection();
    return () => {
      stopDetection();
    };
  }, [autoStart, startDetection, stopDetection]);

  return {
    hasMultipleScreens,
    isScreenDetailsSupported,
    startDetection,
    stopDetection,
  };
};

